import { apiAxios } from '@/api/apiConfig';

export interface TeamSummary {
  teamId: number;
  teamName: string;
  myRole: 'OWNER' | 'ADMIN' | 'MEMBER';
  memberCount?: number;
  plan?: string;
  monthlyLimit?: number;
  status?: string;
}

export interface TeamDetail extends TeamSummary {
  ownerUserId: string;
  createdAt: string;
}

export interface TeamMember {
  userId: string;
  name: string;
  email: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  status: 'ACTIVE' | 'PENDING_REMOVAL';
  joinedAt: string;
}

export interface TeamUsage {
  teamId: number;
  teamName: string;
  month: string;
  totalUsed: number;
  monthlyLimit: number;
  remaining: number;
  memberStats: {
    userId: string;
    name: string;
    role: string;
    usedCount: number;
  }[];
}

export interface InviteAcceptResult {
  teamId: number;
  teamName: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  joinedAt: string;
}

export interface InviteVerifyResult {
  valid: boolean;
  teamId?: number;
  teamName?: string;
  role?: 'OWNER' | 'ADMIN' | 'MEMBER';
  invitedEmail?: string;
  expiresAt?: string;
  message?: string;
}

export interface RemoveMemberResult {
  removed: boolean;
  pendingRemoval: boolean;
}

export interface TransferOwnerResult {
  newOwner: { userId: string; role: 'OWNER' };
  prevOwner: { userId: string; role: 'ADMIN' };
}

export const teamApi = {
  getMyTeams: () =>
    apiAxios.get<{ success: boolean; data: TeamSummary[] }>('/teams/my'),

  getAdminTeams: () =>
    apiAxios.get<{ success: boolean; data: TeamSummary[] | { content: TeamSummary[] } }>('/admin/teams?page=0&size=100'),

  getTeam: (teamId: number) =>
    apiAxios.get<{ success: boolean; data: TeamDetail }>(`/teams/${teamId}`),

  createTeam: (name: string) =>
    apiAxios.post<{ success: boolean; data: TeamDetail }>('/teams', { name }),

  updateTeam: (teamId: number, name: string) =>
    apiAxios.patch<{ success: boolean; data: TeamDetail }>(`/teams/${teamId}`, { name }),

  deleteTeam: (teamId: number) =>
    apiAxios.delete<{ success: boolean }>(`/teams/${teamId}`),

  getMembers: (teamId: number) =>
    apiAxios.get<{ success: boolean; data: TeamMember[] }>(`/teams/${teamId}/members`),

  inviteMember: (teamId: number, email: string, role: 'ADMIN' | 'MEMBER') =>
    apiAxios.post<{ success: boolean; message?: string }>(
      `/teams/${teamId}/invites`, { invitedEmail: email, role }
    ),

  verifyInvite: (token: string) =>
    apiAxios.get<{ success: boolean; data: InviteVerifyResult }>(
      `/teams/invites/${token}/verify`
    ),

  acceptInvite: (token: string) =>
    apiAxios.post<{ success: boolean; data: InviteAcceptResult }>(
      `/teams/invites/${token}/accept`, {}, { _skipAuthRedirect: true } as any
    ),

  cancelInvite: (inviteToken: string) =>
    apiAxios.delete<{ success: boolean }>(`/teams/invites/${inviteToken}`),

  changeMemberRole: (teamId: number, userId: string, role: 'ADMIN' | 'MEMBER') =>
    apiAxios.patch<{ success: boolean }>(
      `/teams/${teamId}/members/${userId}/role`, { newRole: role }
    ),

  removeMember: (teamId: number, userId: string) =>
    apiAxios.delete<{ success: boolean; data: RemoveMemberResult }>(
      `/teams/${teamId}/members/${userId}`
    ),

  transferOwner: (teamId: number, newOwnerUserId: string) =>
    apiAxios.patch<{ success: boolean; data: TransferOwnerResult }>(
      `/teams/${teamId}/transfer-owner`, { newOwnerUserId }
    ),

  getUsage: (teamId: number, month?: string) =>
    apiAxios.get<{ success: boolean; data: TeamUsage }>(
      `/teams/${teamId}/usage${month ? `?month=${month}` : ''}`
    ),
};
